/**
 * EL ARMADOR, ITEM POR ITEM (MADERINCO-REDISENO-T1 iteracion 0, Ares #27621; diagnostico P 8b6c4e07).
 *
 * Tres cosas que el armador hacia mal y que se arreglan aqui, sin DOM (lo prueba scripts/check-armador-presupuestos.mjs):
 *   1. El precio por mayor del catalogo (precio_mayor desde N unidades) nunca se aplicaba: el armador tomaba siempre el
 *      de venta. Ahora el precio del catalogo sale del TRAMO segun la cantidad, y cambia solo si cambia la cantidad.
 *   2. Un item agregado no se podia editar, solo quitar. Ahora se cambia la cantidad o el precio; un precio cambiado a
 *      mano queda marcado "tecleado" y ya no lo mueve el tramo (el 51 % de los precios de Maderinco va a mano).
 *   3. "Solicitar PDF" era el unico guardar y NUNCA pedia el PDF: hacia POST /presupuestos (queda en borrador) y decia
 *      "el equipo te envia el PDF". Ahora guardar y pedir el PDF son dos cosas; pedirlo guarda y despues llama
 *      POST /{id}/solicitar-pdf, que es lo que de verdad marca el pedido.
 *
 * Lo guardado sigue mandado por el servidor (la vista pinta su resumen). Esto solo arma lo que aun no se guarda.
 */

/** El precio del catalogo para una cantidad: el de mayor si hay tramo y la cantidad llega; si no, el de venta. */
export function precioDelCatalogo(prod, cantidad) {
  const venta = Number(prod?.precioVenta ?? prod?.precio_venta ?? 0) || 0;
  const mayor = Number(prod?.precioMayor ?? prod?.precio_mayor ?? 0) || 0;
  const desde = Number(prod?.mayorDesdeUnidades ?? prod?.mayor_desde_unidades ?? 0) || 0;
  const cant = Number(cantidad) || 0;
  if (mayor > 0 && desde > 0 && cant >= desde) return { precio: mayor, origen: 'catalogo_mayor' };
  return { precio: venta, origen: 'catalogo' };
}

/** Un item nuevo desde un producto del catalogo. Guarda el tramo para poder recalcularlo al cambiar la cantidad. */
export function itemDesdeProducto(prod, cantidad) {
  const cant = Number(cantidad) || 0;
  const p = precioDelCatalogo(prod, cant);
  return {
    titulo: prod?.nombre ?? '',
    productoId: prod?.id ?? null,
    unidad: prod?.unidad || '',
    cantidad: cant,
    precioUnit: p.precio,
    precioOrigen: p.origen,
    tramo: {
      precioVenta: Number(prod?.precioVenta ?? prod?.precio_venta ?? 0) || 0,
      precioMayor: Number(prod?.precioMayor ?? prod?.precio_mayor ?? 0) || 0,
      mayorDesdeUnidades: Number(prod?.mayorDesdeUnidades ?? prod?.mayor_desde_unidades ?? 0) || 0
    }
  };
}

/** Cambia la cantidad. Si el precio vino del catalogo, el tramo se vuelve a mirar; si se tecleo, se respeta. */
export function cambiarCantidad(item, cantidad) {
  const cant = Number(cantidad);
  if (!(cant > 0)) return { ...item };
  const nuevo = { ...item, cantidad: cant };
  // REPLICAR-PRESUPUESTO-T1: la cantidad de una partida replicada que se escribe queda confirmada.
  if (item.porConfirmar) nuevo.porConfirmar = false;
  if (item.precioOrigen !== 'tecleado' && item.tramo) {
    const p = precioDelCatalogo(item.tramo, cant);
    nuevo.precioUnit = p.precio;
    nuevo.precioOrigen = p.origen;
  }
  return nuevo;
}

/** Cambia el precio a mano: queda "tecleado" y el tramo ya no lo mueve. Un precio negativo no entra. */
export function cambiarPrecio(item, precio) {
  const p = Number(precio);
  if (!(p >= 0) || !Number.isFinite(p)) return { ...item };
  if (p === Number(item.precioUnit) && item.precioOrigen !== 'tecleado') return { ...item };
  return { ...item, precioUnit: p, precioOrigen: 'tecleado' };
}

/** Lo que se dice al lado del precio. */
export function origenEnPalabras(item) {
  if (item?.precioOrigen === 'catalogo_mayor') return 'precio por mayor';
  if (item?.precioOrigen === 'tecleado') return 'precio a mano';
  return '';
}

/**
 * GUARDAR, Y PEDIR EL PDF SOLO SI SE PIDE. `api` es PresupuestosApiClient (o uno de prueba con la misma forma).
 * Devuelve lo guardado (id, codigo, version, total del servidor) y si el pedido de PDF quedo hecho. Un guardado que
 * falla no pide PDF; un pedido de PDF que falla deja lo guardado guardado y lo dice.
 */
export async function guardarPresupuesto(api, cuerpo, { pedirPdf = false } = {}) {
  const res = await api.crearPresupuesto(cuerpo);
  if (!res || !res.ok) return { ok: false, error: res?.error ?? null, pdfPedido: false };
  const creado = res.data?.data ?? res.data ?? null;
  const id = creado?.id ?? creado?.Id ?? null;
  const codigo = creado?.codigo ?? creado?.Codigo ?? '';
  const version = Number(creado?.version ?? creado?.Version ?? cuerpo?.version ?? 1) || 1;
  const eco = creado?.resumen ?? creado?.Resumen ?? null;
  const totalServidor = eco ? Number(eco.total ?? eco.Total) : NaN;
  let pdfPedido = false;
  let pdfError = null;
  if (pedirPdf && id) {
    const r = await api.solicitarPdf(id);
    pdfPedido = !!(r && r.ok);
    if (!pdfPedido) pdfError = r?.error ?? null;
  }
  return { ok: true, id, codigo, version, totalServidor, pdfPedido, pdfError };
}

/**
 * CODIGO Y VERSION AL GUARDAR (hallazgo #27982, audit 01d15ce1): un presupuesto NUEVO se guardaba sin codigo y la
 * columna es NOT NULL (500). Regla:
 *   - abierto de uno guardado (trae version): el MISMO codigo, version + 1;
 *   - nuevo: el codigo que escribio el usuario, version 1;
 *   - sin codigo: null -> no se guarda y se pide.
 * El formato del codigo no se inventa aqui (ej. JJ 1347 es solo el ejemplo del campo).
 * @returns {null|{codigo:string, version:number}}
 */
export function codigoYVersion(armador) {
  const codigo = String((armador && armador.codigo) || '').trim();
  if (!codigo) return null;
  const v = Number(armador.version);
  return { codigo, version: Number.isInteger(v) && v > 0 ? v + 1 : 1 };
}
